import { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../../utils/axios";
import Sidebar from "./sidebar";
import { getUser } from "../../utils/auth";
import resolveImageUrl from "../../utils/imageUtils";
import {
  trackAction,
  collectFeatureChanges,
  collectInspectorText,
  buildClickDetails,
  FEATURE_LABELS,
} from "../../utils/clickTracking";
import { getPropertyUpdateErrorMessage } from "../../utils/propertyErrors";
import DuplicatePt5Modal from "../../components/DuplicatePt5Modal";
import { useDuplicatePt5Retry } from "../../hooks/useDuplicatePt5Retry";
import { getStarredProperties, toggleStarProperty } from "../../utils/starredProperty";
import toast, { Toaster } from "react-hot-toast";
import { AnimatePresence, motion } from "framer-motion";

const ITEMS_PER_PAGE = 12;

const appendCacheBuster = (url, buster) => {
  if (!url || !buster) return url;
  return url.includes("?") ? `${url}&t=${buster}` : `${url}?t=${buster}`;
};

const Assigned = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const targetPinFromQuery = searchParams.get("pin")?.trim() || "";
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showFullImage, setShowFullImage] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [savingImageRotation, setSavingImageRotation] = useState(false);

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setRotation(0);
    navigate({ search: "" }, { replace: true });
  };

  const handleRotate = (e) => {
    e.stopPropagation();
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleSaveRotation = async (e) => {
    e.stopPropagation();
    if (!selectedProperty?.pin || rotation === 0 || savingImageRotation) return;

    const toastId = toast.loading("Saving rotated image...");
    setSavingImageRotation(true);
    
    try {
      // We need to call rotate-image multiple times if rotation is more than 90 degrees
      const totalRotations = Math.abs(rotation / 90);
      for (let i = 0; i < totalRotations; i++) {
        await axios.post(`/properties/rotate-image-id/${selectedProperty.id}`, { 
          pin: selectedProperty.pin, 
          rotateBy: 90, 
          forcePortrait: false 
        });
      }
      
      const bust = Date.now();
      setProperties((prev) =>
        prev.map((p) => (p.pin === selectedProperty.pin ? { ...p, _imgBust: bust } : p))
      );
      setSelectedProperty((prev) =>
        prev?.pin === selectedProperty.pin ? { ...prev, _imgBust: bust } : prev
      );
      setRotation(0);
      toast.success("Image saved successfully", { id: toastId });
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to save image", {
        id: toastId,
      });
    } finally {
      setSavingImageRotation(false);
    }
  };

  const [showReassessModal, setShowReassessModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [features, setFeatures] = useState([]);
  const [filter, setFilter] = useState("all"); // "all", "starred", "unstarred"
  const [showRefModal, setShowRefModal] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState("");
  const [readyToSubmit, setReadyToSubmit] = useState(false);
  const [showNotReassessModal, setShowNotReassessModal] = useState(false);
  const [notReassessComment, setNotReassessComment] = useState("");
  const [overallExplanation, setOverallExplanation] = useState("");
  const [autoOpenedPin, setAutoOpenedPin] = useState("");
  const [initialAfterValues, setInitialAfterValues] = useState({});
  const {
    showDuplicatePt5Modal,
    openDuplicatePt5Modal,
    closeDuplicatePt5Modal,
    saveDuplicatePt5Retry,
  } = useDuplicatePt5Retry();

  const applyNewPt5 = (newPt5) => {
    setReferenceNumber(newPt5);
    setFeatures((prev) =>
      prev.map((f) => (f.key === "pt" ? { ...f, after: newPt5 } : f))
    );
  };




  const [user, setUser] = useState(getUser());
  const userId = user?.id;
  const normalizePin = (pin) => String(pin || "").trim().toLowerCase();
  const [starredPins, setStarredPins] = useState([]);
  const [starredLoading, setStarredLoading] = useState(true);
  const [starredError, setStarredError] = useState(null);

  // Check for user changes and load starred properties
  useEffect(() => {
    const checkUser = () => {
      const currentUser = getUser();
      console.log('👤 assigned.jsx: checkUser called, currentUser:', currentUser);
      setUser(currentUser);
      
      // Load starred properties when user is available
      if (currentUser?.id) {
        console.log('👤 User found, loading starred properties now...');
        loadStarred(currentUser.id);
      } else {
        console.log('👤 No user found, clearing starredPins');
        setStarredPins([]);
        setStarredLoading(false);
      }
    };
    
    const loadStarred = async (uid) => {
      console.log('📌 assigned.jsx: Loading starred properties for current user, uid:', uid);
      setStarredLoading(true);
      try {
        const res = await getStarredProperties();
        console.log('📌 assigned.jsx: getStarredProperties response:', res);
        if (res.success) {
          const normalizedPins = res.starredPins.map(normalizePin);
          console.log('📌 assigned.jsx: Setting starredPins to:', normalizedPins);
          setStarredPins(normalizedPins);
        }
      } catch (err) {
        console.error("❌ assigned.jsx: error loading starred properties", err);
        setStarredError(err);
      } finally {
        setStarredLoading(false);
      }
    };
    
    // Check on mount, when storage changes (other tabs), and when auth changes (same tab)
    checkUser();
    window.addEventListener('storage', checkUser);
    window.addEventListener('authChange', checkUser);
    
    return () => {
      window.removeEventListener('storage', checkUser);
      window.removeEventListener('authChange', checkUser);
    };
  }, []);
  const dedupeByPin = (list) => {
    const seen = new Set();
    return (list || []).filter((p) => {
      const key = normalizePin(p?.pin);
      if (!key) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const hasUsableValue = (value) => {
    if (value === null || value === undefined) return false;
    const str = String(value).trim().toLowerCase();
    return str !== "" && str !== "null" && str !== "undefined";
  };

  const toFiniteNumber = (val) => {
    if (val === null || val === undefined) return null;
    if (typeof val === "number") return Number.isFinite(val) ? val : null;
    const n = Number(String(val).replaceAll(",", "").trim());
    return Number.isFinite(n) ? n : null;
  };

  const formatTwoDecimals = (value) => {
    if (value === null || value === undefined) return "";
    const str = String(value).trim();
    if (!str) return "";
    const lowered = str.toLowerCase();
    if (lowered === "null" || lowered === "undefined") return "";
    const n = toFiniteNumber(str);
    if (n === null) return str;
    return String(Math.round(n));
  };

  const normalizeId = (val) => String(val ?? "").trim();

  const isReassessYes = (val) =>
    val === 1 || val === "1" || val === true || String(val).toLowerCase() === "true";

  const isReassessNo = (val) =>
    val === 0 || val === "0" || val === false || String(val).toLowerCase() === "false";

  const formatWorkflowStatus = (raw) => {
    if (!hasUsableValue(raw)) return null;
    const val = String(raw).trim();
    const key = val.toLowerCase();
    const labels = {
      new: "New",
      resolved: "Resolved",
      flagged: "Flagged",
      assigned: "Assigned",
      explained: "Explained",
    };
    return labels[key] || val;
  };

  const getPrimaryStatusBadge = (property) => {
    if (!property) return null;

    const workflowKey = hasUsableValue(property.status)
      ? String(property.status).trim().toLowerCase()
      : "";

    if (workflowKey === "resolved") {
      return { key: "status", label: "Resolved", className: "bg-green-100 text-green-800" };
    }

    if (workflowKey === "flagged") {
      return { key: "status", label: "Flagged", className: "bg-yellow-100 text-yellow-800" };
    }

    const updatedBy = property.updated_by;
    const isReviewed = hasUsableValue(updatedBy);
    const isReviewedByYou = isReviewed && normalizeId(updatedBy) === normalizeId(userId);

    if (!isReviewedByYou) {
      return { key: "status", label: "Pending Review", className: "bg-blue-100 text-blue-800" };
    }

    if (isReassessYes(property.reassess)) {
      return { key: "status", label: "Reassessed", className: "bg-green-100 text-green-800" };
    }

    if (isReassessNo(property.reassess)) {
      return { key: "status", label: "Not Reassessed", className: "bg-red-100 text-red-800" };
    }

    return { key: "status", label: "Reviewed", className: "bg-slate-100 text-slate-700" };
  };

  const getStatusBadges = (property) => {
    const badge = getPrimaryStatusBadge(property);
    return badge ? [badge] : [];
  };

  // Save starred pins to localStorage whenever they change
  // REMOVED localStorage, now using API
  // Clean up starred pins: remove any pins that are no longer in pending properties
  // REMOVED cleanup, API handles storage

  // Toggle star status
  const toggleStar = async (pin) => {
    console.log('⭐ toggleStar called with pin:', pin, 'type:', typeof pin);
    const normalizedPin = normalizePin(pin);
    console.log('⭐ normalizedPin:', normalizedPin);
    console.log('⭐ Current starredPins:', starredPins);

    if (!userId) {
      toast.error("User not logged in");
      return;
    }

    const wasStarred = starredPins.includes(normalizedPin);
    const actionName = wasStarred ? "unstar" : "star";
    
    // Track the star/unstar action with PIN
    await trackAction(actionName, normalizedPin, null, {
      star_action: actionName,
      property_id: normalizedPin
    });
    
    console.log('⭐ wasStarred:', wasStarred);
    
    // Optimistic update
    setStarredPins(prev =>
      wasStarred
        ? prev.filter(p => p !== normalizedPin)
        : [...prev, normalizedPin]
    );
    console.log('⭐ After optimistic update, starredPins set to:', 
      wasStarred 
        ? starredPins.filter(p => p !== normalizedPin) 
        : [...starredPins, normalizedPin]
    );

    try {
      const res = await toggleStarProperty(normalizedPin);
      console.log('⭐ toggleStarProperty response:', res);
      if (res.success && res.starredPins) {
        const normalizedFromApi = res.starredPins.map(normalizePin);
        console.log('⭐ Setting starredPins from API:', normalizedFromApi);
        setStarredPins(normalizedFromApi);
      }
    } catch (err) {
      console.error("❌ assigned.jsx: error toggling star", err);
      toast.error("Failed to update starred property");
      // Revert optimistic update
      setStarredPins(prev =>
        wasStarred
          ? [...prev, normalizedPin]
          : prev.filter(p => p !== normalizedPin)
      );
      console.log('⭐ Reverted optimistic update, starredPins:', 
        wasStarred 
          ? [...starredPins, normalizedPin] 
          : starredPins.filter(p => p !== normalizedPin)
      );
    }
  };

  // Check if a property is starred
  const isStarred = (pin) => {
    const normalized = normalizePin(pin);
    const includes = starredPins.includes(normalized);
    console.log('⭐ isStarred check: pin=', pin, 'normalized=', normalized, 'starredPins=', starredPins, 'includes=', includes);
    return includes;
  };

  // Fetch Assigned Properties
  const fetchProperties = async () => {
    try {
      if (!userId) return setError("Inspector ID missing.");
      setLoading(true);
      const res = await axios.get(`/properties/assignments/inspector-notfiltered/${userId}`);
      setProperties(dedupeByPin(res.data || []));
    } catch (err) {
      console.error(err);
      setError("Failed to load properties");
      toast.error("Failed to load assigned properties");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    if (!targetPinFromQuery || !properties.length) return;
    if (autoOpenedPin && autoOpenedPin === targetPinFromQuery) return;

    const matchedProperty = properties.find(
      (p) => String(p.pin || "").trim().toLowerCase() === targetPinFromQuery.toLowerCase()
    );

    if (matchedProperty) {
      setSelectedProperty(matchedProperty);
      setShowDetailModal(true);
      setAutoOpenedPin(targetPinFromQuery);
    }
  }, [targetPinFromQuery, properties, autoOpenedPin]);

  // First filter out properties that have already been reviewed by this inspector
  const pendingProperties = useMemo(() => {
    return properties.filter((prop) => {
      const r = String(prop?.reassess ?? "").trim().toLowerCase();
      const isReviewed = r === "0" || r === "1" || r === "true" || r === "false";
      return !isReviewed;
    });
  }, [properties, userId]);

  // Calculate starred count for pending properties
  const starredCountInPending = useMemo(() => {
    const pendingStarredProps = pendingProperties.filter(prop => isStarred(prop.pin));
    console.log('🔍 pendingProperties:', pendingProperties.map(p => ({ pin: p.pin, id: p.id })));
    console.log('🔍 starredPins:', starredPins);
    console.log('🔍 pendingStarredProps:', pendingStarredProps.map(p => p.pin));
    console.log('🔍 pendingStarredProps.length:', pendingStarredProps.length);
    return pendingStarredProps.length;
  }, [pendingProperties, starredPins]);

  // Filter properties based on star status
  const filteredProperties = useMemo(() => {
    let result;
    switch (filter) {
      case "starred":
        result = pendingProperties.filter(prop => isStarred(prop.pin));
        break;
      case "unstarred":
        result = pendingProperties.filter(prop => !isStarred(prop.pin));
        break;
      default:
        result = pendingProperties;
    }
    console.log('🔍 filter:', filter);
    console.log('🔍 filteredProperties:', result.map(p => p.pin));
    console.log('🔍 filteredProperties.length:', result.length);
    return result;
  }, [filter, pendingProperties, starredPins]);

  // Pagination calculations based on filtered properties
  const totalPages = Math.ceil(filteredProperties.length / ITEMS_PER_PAGE);
  const paginated = filteredProperties.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  // Reset page when filter changes
  useEffect(() => {
    setPage(1);
  }, [filter]);

  // Build features for reassessment modal
  const setFeaturesForReassess = (p) => {
    const newFeatures = [
      { 
        key: "floor", 
        label: "Floor", 
        before: p.floor_admin || "", 
        after: p.floors_pred ?? "",
        unit: ""
      },
      { 
        key: "landuse", 
        label: "Land Use", 
        before: p.landuse_admin || "", 
        after: p.land_use_pred ?? "",
        unit: ""
      },
      { 
        key: "landarea", 
        label: "Land Area", 
        before: p.landarea_admin || "", 
        after: p.landarea_combined_pred ?? "",
        unit: "sq.ft"
      },
      { 
        key: "coveredarea", 
        label: "Covered Area", 
        before: p.coveredarea_admin || "", 
        after: p.coveredarea_combined_pred ?? "",
        unit: "sq.ft"
      },
      { 
        key: "pt", 
        label: "PT-5 Serial Number", 
        before: " ", 
        after: p.refno || "",
        unit: ""
      },
    ];
    // Save initial after values!
    const initialAfter = {};
    newFeatures.forEach(f => {
      initialAfter[f.key] = f.after;
    });
    setFeatures(newFeatures);
    setInitialAfterValues(initialAfter);
  };

  const updateFeature = (index, field, value) => {
    setFeatures((prev) => {
      const updated = [...prev];
      const f = updated[index];
      
      // Validate based on field type
      let validatedValue = value;
      if (field === "after") {
        switch (f.key) {
          case "floor":
          case "landarea":
          case "coveredarea":
            // Should be a number (allow only digits, optional decimal)
            if (/[^0-9.]/.test(value)) {
              toast.error("Please enter only numbers for this field!");
              validatedValue = value.replace(/[^0-9.]/g, "");
            }
            // Prevent multiple decimals
            if ((validatedValue.match(/\./g) || []).length > 1) {
              validatedValue = validatedValue.slice(0, -1);
            }
            // Validate area ranges
            if (validatedValue) {
              const numVal = parseFloat(validatedValue);
              if (f.key === "landarea") {
                // Land Area (sq yards) ranges: 0-31, 32-91, 92-175, 176-300, 301-108000
                if (numVal < 0 || numVal > 108000) {
                  toast.error("Land Area must be between 0 and 108,000 sq yards!");
                }
              } else if (f.key === "coveredarea") {
                // Covered Area (sq ft) ranges: 0-400, 401-977, 978-1710, 1711-2920, 2921-272000
                if (numVal < 0 || numVal > 272000) {
                  toast.error("Covered Area must be between 0 and 272,000 sq ft!");
                }
              }
            }
            break;
          case "landuse":
            // Should be text only (no numbers)
            if (/[0-9]/.test(value)) {
              toast.error("Please enter only letters for Land Use!");
              validatedValue = value.replace(/[0-9]/g, "");
            }
            break;
        }
      }
      
      updated[index][field] = validatedValue;
      return updated;
    });
  };

  const handleNotReassess = async (refnoOverride = null) => {
    try {
      setSubmitting(true);

      const fd = new FormData();

      fd.append("pin", selectedProperty.pin);
      fd.append("userId", userId);
      fd.append("reassess", "0");
      fd.append("comment", notReassessComment);

      // Send ALL required admin fields (if exist)
      fd.append("circle", selectedProperty.circle || "");
      fd.append("occupationstatus", selectedProperty.occupationstatus || "");
      fd.append("location", selectedProperty.location || "");
      fd.append("floor_admin", selectedProperty.floor_admin || "");
      fd.append("landuse_admin", selectedProperty.landuse_admin || "");
      fd.append("landarea_admin", selectedProperty.landarea_admin || "");
      fd.append("coveredarea_admin", selectedProperty.coveredarea_admin || "");
      fd.append("admin_garv", selectedProperty.admin_garv || "");
      
      // Preserve the reference number
      const refVal = refnoOverride || referenceNumber || selectedProperty.refno || selectedProperty.pt5 || selectedProperty.pt_5 || selectedProperty.pt || "";
      if (refVal) {
        fd.append("refno", refVal);
      }

      await axios.put(`/properties/notfiltered-id/${selectedProperty.id}`, fd);
      
      // Track the do not reassess action!
      await trackAction(
        "do_not_reassess",
        selectedProperty.pin,
        null,
        {
          comment_text: notReassessComment?.trim()
            ? `Reason for not reassessing: ${notReassessComment.trim()}`
            : "No reason provided"
        }
      );

      toast.success("Successfully marked as 'Do Not Reassess'");
      setNotReassessComment("");
      setShowNotReassessModal(false);

      setShowDetailModal(false);
      fetchProperties();

    } catch (err) {
      console.error(err);
      if (openDuplicatePt5Modal(err, async (newPt5) => {
        applyNewPt5(newPt5);
        await handleNotReassess(newPt5);
      })) {
        return;
      }
      toast.error(getPropertyUpdateErrorMessage(err, "Failed to update status"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitReassess = async ({ featuresOverride, refnoOverride } = {}) => {
    const activeFeatures = featuresOverride ?? features;

    try {
      setSubmitting(true);

      const fd = new FormData();

      fd.append("pin", selectedProperty.pin);
      fd.append("userId", userId);
      fd.append("reassess", "1");

      // Required admin values (existing)
      fd.append("circle", selectedProperty.circle || "");
      fd.append("occupationstatus", selectedProperty.occupationstatus || "");
      fd.append("location", selectedProperty.location || "");
      fd.append("floor_admin", selectedProperty.floor_admin || "");
      fd.append("landuse_admin", selectedProperty.landuse_admin || "");
      fd.append("landarea_admin", selectedProperty.landarea_admin || "");
      fd.append("coveredarea_admin", selectedProperty.coveredarea_admin || "");
      fd.append("admin_garv", selectedProperty.admin_garv || "");
      
      // Collect feature changes for the comment!
      const featureChanges = [];

      // Updated prediction values
      activeFeatures.forEach((f) => {
        const val = f.key === "pt" && refnoOverride
          ? refnoOverride
          : f.after?.toString().trim();
        const initialVal = initialAfterValues[f.key]?.toString().trim();
        const isModified = val !== initialVal;
        if (isModified) {
          let displayName;
          switch(f.key) {
            case "floor": displayName = "Number of Floors"; break;
            case "landuse": displayName = "Land Use"; break;
            case "landarea": displayName = "Land Area"; break;
            case "coveredarea": displayName = "Covered Area"; break;
            case "pt": displayName = "PT-5 Serial Number"; break;
            default: displayName = f.key; break;
          }
          featureChanges.push(`${displayName} changed from "${initialVal || 'Not set'}" to "${val || 'Not set'}"`);
        }
        
        if (!val) return;

        switch (f.key) {
          case "floor":
            fd.append("floors_pred", val);
            break;
          case "landuse":
            fd.append("land_use_pred", val);
            break;
          case "landarea":
            fd.append("landarea_combined_pred", val);
            break;
          case "coveredarea":
            fd.append("coveredarea_combined_pred", val);
            break;
          case "pt":
            fd.append("refno", val);
            break;
        }
      });

      // exp_garv should also be included if it exists
      if (selectedProperty.exp_garv)
        fd.append("exp_garv", selectedProperty.exp_garv);

      await axios.put(`/properties/notfiltered-id/${selectedProperty.id}`, fd);
      
      // Extract individual explanations
      let floorExp = "";
      let landuseExp = "";
      let landareaExp = "";
      let coveredareaExp = "";

      // Check features for explanations
      activeFeatures.forEach(feature => {
        if (feature.explanation) {
          switch (feature.key) {
            case "floor": floorExp = feature.explanation; break;
            case "landuse": landuseExp = feature.explanation; break;
            case "landarea": landareaExp = feature.explanation; break;
            case "coveredarea": coveredareaExp = feature.explanation; break;
          }
        }
      });

      // Track each explanation as a separate row
      if (overallExplanation) {
        await trackAction(
          "Overall Explanation",
          selectedProperty.pin,
          null,
          {
            overall_explanation: overallExplanation
          }
        );
      }
      if (floorExp) {
        await trackAction(
          "Number of Floors Explanation",
          selectedProperty.pin,
          null,
          {
            floor_explanation: floorExp
          }
        );
      }
      if (landuseExp) {
        await trackAction(
          "Land Use Explanation",
          selectedProperty.pin,
          null,
          {
            landuse_explanation: landuseExp
          }
        );
      }
      if (landareaExp) {
        await trackAction(
          "Land Area Explanation",
          selectedProperty.pin,
          null,
          {
            landarea_explanation: landareaExp
          }
        );
      }
      if (coveredareaExp) {
        await trackAction(
          "Covered Area Explanation",
          selectedProperty.pin,
          null,
          {
            coveredarea_explanation: coveredareaExp
          }
        );
      }

      // Track each feature change separately (use for...of to preserve order)
      for (const feature of activeFeatures) {
        const oldVal = initialAfterValues[feature.key]?.toString().trim() || "";
        const newVal = feature.after?.toString().trim() || "";
        if (oldVal !== newVal) {
          const featureName = FEATURE_LABELS[feature.key] || feature.label || feature.key;
          await trackAction(
            `${feature.key}_updated`,
            selectedProperty.pin,
            null,
            {
              old_value: oldVal,
              new_value: newVal,
              comment_text: `${featureName} changed from "${oldVal}" to "${newVal}"`
            }
          );
        }
      }

      await trackAction("reassessed", selectedProperty.pin);
      toast.success("Reassessment saved successfully!");
      setShowReassessModal(false);
      setShowRefModal(false);
      fetchProperties();

    } catch (err) {
      console.error(err);
      if (openDuplicatePt5Modal(err, async (newPt5) => {
        const updatedFeatures = activeFeatures.map((f) =>
          f.key === "pt" ? { ...f, after: newPt5 } : f
        );
        applyNewPt5(newPt5);
        await handleSubmitReassess({
          featuresOverride: updatedFeatures,
          refnoOverride: newPt5,
        });
      })) {
        return;
      }
      toast.error(getPropertyUpdateErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
  if (!readyToSubmit || submitting) return;

  handleSubmitReassess();
  setReadyToSubmit(false);
}, [readyToSubmit, submitting]);

const isReassessValid = features
  .filter(f => f.key !== "pt") // ignore pt
  .some(f => f.after && f.after.toString().trim() !== "");



  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <Sidebar />

      <main className="flex-1 p-4 md:p-6 overflow-y-auto">
        <Toaster position="top-right" />

        {/* Header Section */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                Pending Review
              </h1>
              <p className="text-gray-600 mt-2">
                Total: <span className="font-semibold">{properties.length}</span> properties
                {filter !== "all" && (
                  <>
                    {" • "}
                    Showing: <span className="font-semibold">{filteredProperties.length}</span> {filter} properties
                  </>
                )}
              </p>
            </div>
            
            <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
              {/* Filter Controls */}
              <div className="flex items-center gap-2 bg-white p-2 rounded-lg shadow">
                <button
                  onClick={() => setFilter("all")}
                  className={`px-3 py-1.5 text-sm rounded transition-colors ${
                    filter === "all"
                      ? "bg-blue-100 text-blue-700 font-medium"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                  data-click-id="pending_review_all"
                >
                  All
                </button>
                <button
                  onClick={() => setFilter("starred")}
                  className={`px-3 py-1.5 text-sm rounded transition-colors flex items-center gap-1.5 ${
                    filter === "starred"
                      ? "bg-yellow-100 text-yellow-700 font-medium"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                  data-click-id="pending_review_starred"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  Starred ({starredCountInPending})
                </button>
                <button
                  onClick={() => setFilter("unstarred")}
                  className={`px-3 py-1.5 text-sm rounded transition-colors ${
                    filter === "unstarred"
                      ? "bg-gray-100 text-gray-700 font-medium"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                  data-click-id="pending_review_unstarred"
                >
                  Unstarred
                </button>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="text-sm text-gray-600 bg-white px-4 py-2 rounded-lg shadow">
                  Showing <span className="font-semibold">{paginated.length}</span> properties
                </div>
                
                <button
                  onClick={fetchProperties}
                  disabled={loading}
                  className="p-2 bg-white hover:bg-gray-100 text-gray-600 rounded-lg shadow transition-colors disabled:opacity-50"
                  title="Refresh"
                  data-click-id="pending_review_refresh"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-600">Loading assigned properties...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-2">
              <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="font-semibold text-red-800">Error Loading Properties</h3>
            </div>
            <p className="text-red-600">{error}</p>
            <button
              onClick={fetchProperties}
              className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && filteredProperties.length === 0 && (
          <div className="text-center py-16 bg-white rounded-xl shadow">
            <div className="text-6xl mb-6">
              {filter === "starred" ? "⭐" : "🏠"}
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              {filter === "starred" 
                ? "No Starred Properties" 
                : filter === "unstarred"
                ? "No Unstarred Properties"
                : "No Properties Assigned"}
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              {filter === "starred" 
                ? "Star properties by clicking the star icon on property cards."
                : filter === "unstarred"
                ? "All properties are currently starred. Try viewing all properties."
                : "You don't have any properties assigned to you yet."}
            </p>
            {filter !== "all" && (
              <button
                onClick={() => setFilter("all")}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                View All Properties
              </button>
            )}
          </div>
        )}

        {/* Property Grid */}
        {!loading && !error && filteredProperties.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {paginated.map((prop) => (
                <motion.div
                  key={prop.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-xl shadow hover:shadow-lg border border-gray-200 overflow-hidden group relative"
                >
                  {/* Star Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleStar(prop.pin);
                    }}
                    className="absolute top-3 right-3 z-10 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow hover:bg-white transition-colors"
                    title={isStarred(prop.pin) ? "Unstar property" : "Star property"}
                  >
                    <svg
                      className={`w-5 h-5 transition-transform duration-300 ${
                        isStarred(prop.pin)
                          ? "fill-yellow-500 text-yellow-500"
                          : "fill-gray-300 text-gray-300 hover:fill-yellow-300 hover:text-yellow-300"
                      }`}
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  </button>

                  {/* Property Image */}
                  <div 
                    className="h-48 bg-gray-100 overflow-hidden cursor-pointer"
                    onClick={() => {
                      setSelectedProperty(prop);
                      setShowDetailModal(true);
                    }}
                  >
                    {prop.image ? (
                      <img
                        src={appendCacheBuster(resolveImageUrl(prop.image), prop._imgBust)}
                        className="h-full w-full object-cover hover:scale-105 transition-transform duration-300"
                        alt="Property"
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center bg-gray-50">
                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Property Details */}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-bold text-lg text-gray-900 truncate pr-2">
                        {prop.pin}
                      </h3>
                      {isStarred(prop.pin) && (
                        <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full flex items-center gap-1">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                          Starred
                        </span>
                      )}
                    </div>
                    
                    <div className="space-y-2 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        </svg>
                        <span>Circle: {prop.circle || "N/A"}</span>
                      </div>

                     {/*  {prop.floor_admin && (
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          <span>Floor: {prop.floor_admin}</span>
                        </div>
                      )} */}
                    </div>

                    <div className="mt-4 pt-3 border-t border-gray-100">
                      <button 
                        onClick={() => {
                          setSelectedProperty(prop);
                          setShowDetailModal(true);
                          trackAction("pending_review_view_details", prop.pin);
                        }}
                        className="w-full py-2 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors flex items-center justify-center gap-1"
                        data-click-id="pending_review_view_details"
                        data-property-id={prop.pin}
                      >
                        View Details
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Simple Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 flex justify-center">
                <div className="flex items-center gap-2 bg-white p-4 rounded-xl shadow">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 disabled:opacity-50"
                    data-click-id="pending_review_page_previous"
                  >
                    Previous
                  </button>
                  
                  <span className="px-4 text-sm text-gray-600">
                    Page <span className="font-semibold">{page}</span> of <span className="font-semibold">{totalPages}</span>
                  </span>
                  
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 disabled:opacity-50"
                    data-click-id="pending_review_page_next"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Detail Modal */}
        <AnimatePresence>
          {showDetailModal && selectedProperty && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4"
              onClick={closeDetailModal}
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-white p-6 rounded-xl w-full max-w-4xl shadow-xl max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-gray-800">
                    Property Details
                  </h2>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleStar(selectedProperty.pin)}
                      className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                      title={isStarred(selectedProperty.pin) ? "Unstar property" : "Star property"}
                      data-click-id="star"
                      data-property-id={selectedProperty.pin}
                    >
                      <svg
                        className={`w-6 h-6 transition-transform duration-300 ${
                          isStarred(selectedProperty.pin)
                            ? "fill-yellow-500 text-yellow-500"
                            : "fill-gray-300 text-gray-300 hover:fill-yellow-300 hover:text-yellow-300"
                        }`}
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    </button>
                    <button
                      onClick={closeDetailModal}
                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full"
                      data-click-id="pending_review_modal_close"
                    >
                      ✕
                    </button>
                  </div>
                </div>

                {/* Property Image */}
                <div className="mb-6">
                  <div 
                    className="relative group h-[500px] bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center cursor-pointer"
                    onClick={() => selectedProperty.image && setShowFullImage(true)}
                  >
                    {selectedProperty.image ? (
                      <>
                        <div 
                          className="w-full h-full transition-transform duration-300 ease-in-out flex items-center justify-center"
                          style={{ transform: `rotate(${rotation}deg)` }}
                        >
                          <img
                            src={appendCacheBuster(resolveImageUrl(selectedProperty.image), selectedProperty._imgBust)}
                            className="max-w-full max-h-full object-contain"
                            alt="Property"
                          />
                        </div>
                        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <span className="text-white bg-black/50 px-3 py-1 rounded-full text-sm font-medium">Click to Enlarge</span>
                        </div>
                        <div className="absolute bottom-4 right-4 flex gap-2 z-10">
                          <button
                            onClick={handleRotate}
                            className="p-3 bg-black/50 hover:bg-black/70 rounded-full text-white transition-all shadow-lg"
                            title="Rotate Image"
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          </button>
                          {rotation !== 0 && (
                            <button
                              onClick={handleSaveRotation}
                              disabled={savingImageRotation}
                              className="p-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 rounded-full text-white transition-all shadow-lg flex items-center gap-2"
                              title="Save Rotation"
                            >
                              {savingImageRotation ? (
                                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              ) : (
                                <>
                                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </>
                    ) : (
                      <span className="text-gray-500">No Image Available</span>
                    )}
                  </div>
                </div>

                {/* Information Section - All Content Below Image */}
                <div className="space-y-8">
                  {/* Basic Information */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 text-gray-800 pb-2 border-b">Basic Information</h3>
                    <div className="space-y-3">
                      {[
                        { label: "PIN", value: selectedProperty.pin },
                        { label: "Circle", value: selectedProperty.circle || "N/A" },
                        { label: "Location", value: selectedProperty.location || "N/A" },
                        { label: "Occupation Status", value: selectedProperty.occupationstatus || "N/A" },
                        {
                          label: "Status",
                          value: (
                            <span className="flex flex-wrap gap-2">
                              {getStatusBadges(selectedProperty).map((badge) => (
                                <span
                                  key={badge.key}
                                  className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.className}`}
                                >
                                  {badge.label}
                                </span>
                              ))}
                            </span>
                          ),
                        },
                      ].map((item, index) => (
                        <div key={index} className="flex justify-between py-2 border-b border-gray-100 last:border-0">
                          <span className="font-medium text-gray-700">{item.label}</span>
                          <span className="text-gray-900">{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Admin & Predicted Data Side by Side */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Admin Data */}
                    <div>
                      <h3 className="text-lg font-semibold mb-4 text-gray-800 pb-2 border-b">Admin Data</h3>
                      <div className="space-y-3">
                        {[
                          { label: "Floor", value: hasUsableValue(selectedProperty.floor_admin) ? selectedProperty.floor_admin : "N/A" },
                          { label: "Land Use", value: hasUsableValue(selectedProperty.landuse_admin) ? selectedProperty.landuse_admin : "N/A" },
                          { label: "Land Area", value: hasUsableValue(selectedProperty.landarea_admin) ? `${formatTwoDecimals(selectedProperty.landarea_admin) || selectedProperty.landarea_admin} sq.yards` : "N/A" },
                          { label: "Covered Area", value: hasUsableValue(selectedProperty.coveredarea_admin) ? `${formatTwoDecimals(selectedProperty.coveredarea_admin) || selectedProperty.coveredarea_admin} sq.ft` : "N/A" },
                        ].map((item, index) => (
                          <div key={index} className="flex justify-between py-2 border-b border-gray-100 last:border-0">
                            <span className="font-medium text-gray-700">{item.label}</span>
                            <span className="text-gray-900">{item.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* ML (Computer Vision Prediction) */}
                    <div>
                      <h3 className="text-lg font-semibold mb-4 text-gray-800 pb-2 border-b">ML (Computer Vision Prediction)</h3>
                      <div className="space-y-3">
                        {[
                          {
                            label: "Floor",
                            admin: selectedProperty.floor_admin,
                            ml:
                              selectedProperty.floors_ml ??
                              selectedProperty.floor_ml ??
                              selectedProperty.floors_pred ??
                              selectedProperty.floor_pred,
                          },
                          {
                            label: "Land Use",
                            admin: selectedProperty.landuse_admin,
                            ml:
                              selectedProperty.landuse_ml ??
                              selectedProperty.land_use_ml ??
                              selectedProperty.land_use_pred ??
                              selectedProperty.landuse_pred,
                          },
                          {
                            label: "Land Area",
                            admin: selectedProperty.landarea_admin,
                            ml:
                              selectedProperty.landarea_ml ??
                              selectedProperty.land_area_ml ??
                              selectedProperty.landarea_combined_pred,
                          },
                          {
                            label: "Covered Area",
                            admin: selectedProperty.coveredarea_admin,
                            ml:
                              selectedProperty.coveredarea_ml ??
                              selectedProperty.covered_area_ml ??
                              selectedProperty.coveredarea_combined_pred,
                          },
                        ].map((item, index) => {
                          const mlVal = item.ml !== null && item.ml !== undefined && String(item.ml).trim() !== "" ? String(item.ml) : null;
                          const adminVal = item.admin !== null && item.admin !== undefined && String(item.admin).trim() !== "" ? String(item.admin).trim().toLowerCase() : null;
                          const mlDisplay =
                            item.label === "Land Area" || item.label === "Covered Area"
                              ? (formatTwoDecimals(item.ml) || mlVal)
                              : mlVal;
                          const isDifferent = mlVal && adminVal && mlVal.trim().toLowerCase() !== adminVal;
                          return (
                            <div
                              key={index}
                              className={`flex justify-between py-2 border-b last:border-0 ${isDifferent ? "border-red-200 bg-red-50 px-2 rounded" : "border-gray-100"}`}
                            >
                              <span className="font-medium text-purple-700">{item.label}</span>
                              <span className={`font-semibold ${isDifferent ? "text-red-600" : "text-purple-900"}`}>
                                {mlDisplay ?? "N/A"}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t">
                  {/* <button
                    onClick={handleNotReassess}
                    disabled={submitting}
                    className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded disabled:opacity-50"
                  >
                    {submitting ? "Processing..." : "Do Not Reassess"}
                  </button> */}
                  <button
  onClick={() => {
    setNotReassessComment("");
    setReferenceNumber(selectedProperty.refno || selectedProperty.pt5 || selectedProperty.pt_5 || selectedProperty.pt || "");
    setShowDetailModal(false);
    setShowNotReassessModal(true);
  }}
  disabled={submitting}
  className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded disabled:opacity-50"
  data-click-id="do_not_reassess"
  data-property-id={selectedProperty.pin}
>
  Do Not Reassess
</button>


                  <button
                    onClick={() => {
                      setFeaturesForReassess(selectedProperty);
                      setReferenceNumber(selectedProperty.refno || selectedProperty.pt5 || selectedProperty.pt_5 || selectedProperty.pt || "");
                      setShowDetailModal(false);
                      setShowReassessModal(true);
                    }}
                    disabled={submitting}
                    className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50"
                    data-click-id="reassess"
                    data-property-id={selectedProperty.pin}
                  >
                    Reassess
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Reassessment Modal */}
        <AnimatePresence>
          {showReassessModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4"
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-white p-6 rounded-xl w-full max-w-3xl shadow-xl"
              >
                <h2 className="text-xl font-bold mb-4 text-gray-800">
                  Reassessment
                </h2>

                <div className="overflow-x-auto mb-6">
                  <table className="w-full border">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="p-3 text-left font-medium">Feature</th>
                        <th className="p-3 text-left font-medium">Current</th>
                        <th className="p-3 text-left font-medium">Updated + Explanation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {features.filter(f => f.key !== "pt").map((f, i) => {
                        const isAreaField = f.key === "landarea" || f.key === "coveredarea";
                        const unit = f.key === "landarea" ? "sq yds" : "sq ft";
                        const explanationClickId =
                          f.key === "floor" ? "floor_explanation"
                          : f.key === "landuse" ? "land_use_explanation"
                          : f.key === "landarea" ? "land_area_explanation"
                          : f.key === "coveredarea" ? "covered_area_explanation"
                          : null;
                        const updatedClickId =
                          f.key === "floor" ? "floor_updated"
                          : f.key === "landuse" ? "land_use_updated_select_land_use"
                          : f.key === "landarea" ? "land_area_updated"
                          : f.key === "coveredarea" ? "covered_area_updated"
                          : null;
                        return (
                          <tr key={i} className="border-b">
                            <td className="p-3 font-medium">{f.label}</td>
                            <td className="p-3">
                              <div className="bg-gray-50 p-2 rounded flex items-center gap-2">
                                {f.before || "Not set"}
                                {isAreaField && f.before && <span className="text-gray-500 text-sm">{unit}</span>}
                              </div>
                            </td>
                            <td className="p-3">
                              {f.key === "landuse" ? (
                                <select
                                  value={f.after || ""}
                                  onChange={(e) => {
                                    updateFeature(i, "after", e.target.value);
                                    // Track which land use option was selected
                                    const landUseClickMap = {
                                      Residential: "land_use_updated_residential",
                                      Mixed: "land_use_updated_mixed",
                                      Commercial: "land_use_updated_commercial",
                                    };
                                    const clickId = landUseClickMap[e.target.value];
                                    if (clickId) {
                                      import("../../utils/clickTracking").then(({ trackAction }) => {
                                        trackAction(clickId, selectedProperty?.pin);
                                      });
                                    }
                                  }}
                                  className="w-full p-2 border rounded"
                                  disabled={submitting}
                                  data-property-id={selectedProperty?.pin}
                                >
                                  <option value="">Select Land Use</option>
                                  <option value="Residential">Residential</option>
                                  <option value="Mixed">Mixed</option>
                                  <option value="Commercial">Commercial</option>
                                </select>
                              ) : isAreaField ? (
                                <div className="flex items-center gap-2">
                                  <input
                                    type="number"
                                    value={f.after || ""}
                                    onChange={(e) => updateFeature(i, "after", e.target.value)}
                                    className="flex-1 p-2 border rounded"
                                    placeholder={`Enter ${f.label.toLowerCase()}...`}
                                    data-property-id={selectedProperty?.pin}
                                  />
                                  <span className="text-gray-500 text-sm whitespace-nowrap">{unit}</span>
                                </div>
                              ) : (
                                <input
                                  type="text"
                                  disabled={f.key === "pt"}
                                  value={f.after || ""}
                                  onChange={(e) => updateFeature(i, "after", e.target.value)}
                                  className={`w-full p-2 border rounded ${
                                    f.key === "pt" ? "bg-gray-100 cursor-not-allowed" : ""
                                  }`}
                                  placeholder={
                                    f.key === "pt"
                                      ? "Entered in next step"
                                      : `Enter ${f.label.toLowerCase()}...`
                                  }
                                  data-property-id={selectedProperty?.pin}
                                />
                              )}
                              {/* Explanation textarea for each field */}
                              {explanationClickId && (
                                <textarea
                                  rows={2}
                                  placeholder={`Explanation for ${f.label.toLowerCase()} change (optional)`}
                                  value={f.explanation || ""}
                                  onChange={(e) => updateFeature(i, "explanation", e.target.value)}
                                  className="w-full mt-2 p-2 border rounded text-sm text-gray-600 resize-none"
                                  data-property-id={selectedProperty?.pin}
                                />
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* <div className="flex gap-3">
                  <button
                    onClick={handleSubmitReassess}
                    disabled={submitting}
                    className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50"
                  >
                    {submitting ? "Saving..." : "Save"}
                  </button>

                  <button
                    onClick={() => setShowReassessModal(false)}
                    className="flex-1 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded"
                  >
                    Cancel
                  </button>
                </div> */}
                {/* Overall explanation */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Overall Explanation <span className="text-gray-400">(optional)</span>
                  </label>
                  <textarea
                    rows={2}
                    placeholder="Enter overall explanation for this reassessment..."
                    value={overallExplanation}
                    onChange={(e) => setOverallExplanation(e.target.value)}
                    className="w-full p-2 border rounded text-sm text-gray-600 resize-none"
                    data-property-id={selectedProperty?.pin}
                  />
                </div>

                <div className="flex gap-3">
  <button
    onClick={() => {
      setReferenceNumber("");
      setShowReassessModal(false);
      setShowRefModal(true);
    }}
    disabled={!isReassessValid}
  className={`flex-1 px-4 py-2 rounded text-white
    ${isReassessValid
      ? "bg-green-600 hover:bg-green-700"
      : "bg-blue-300 cursor-not-allowed"
    }`}
  >
    Next
  </button>

  <button
    onClick={() => { setShowReassessModal(false); setOverallExplanation(""); }}
    className="flex-1 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded"
    data-click-id="reassessment canceled"
  >
    Cancel
  </button>
</div>


              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <DuplicatePt5Modal
          isOpen={showDuplicatePt5Modal}
          onClose={closeDuplicatePt5Modal}
          onSave={saveDuplicatePt5Retry}
          submitting={submitting}
        />

        {/* Reference Number Modal */}
        <AnimatePresence>
  {showRefModal && selectedProperty && (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.9 }}
        className="bg-white p-6 rounded-xl w-full max-w-md shadow-xl"
      >
        <h2 className="text-xl font-bold mb-4 text-gray-800">
          {selectedProperty.refno || selectedProperty.pt5 || selectedProperty.pt_5 || selectedProperty.pt 
            ? "Edit PT-5 Serial Number" 
            : "Enter PT-5 Serial Number"}
        </h2>

        <input
          type="text"
          value={referenceNumber}
          onChange={(e) => setReferenceNumber(e.target.value)}
          placeholder="PT-5 Serial Number"
          className="w-full p-3 border rounded mb-2"
        />

        {!referenceNumber && (
          <p className="text-red-500 text-sm mb-3">
            Reference number is required
          </p>
        )}

        <div className="flex gap-3 mt-4">
          <button
  onClick={async () => {
    if (!referenceNumber) return;

    // Get previous PT5 value
    const previousPt5 = selectedProperty.refno || selectedProperty.pt5 || selectedProperty.pt_5 || selectedProperty.pt || "";
    
    // Track the pt5_save action with feature changes
    if (previousPt5 !== referenceNumber) {
      await trackAction(
        "pt5_save",
        selectedProperty.pin,
        buildClickDetails({
          featureChanges: [`PT-5 Serial Number changed from "${previousPt5 || 'Not set'}" to "${referenceNumber || 'Not set'}"`]
        })
      );
    } else {
      await trackAction("pt5_save", selectedProperty.pin);
    }

    setFeatures(prev =>
      prev.map(f =>
        f.key === "pt" ? { ...f, after: referenceNumber } : f
      )
    );

    setShowRefModal(false);
    setReadyToSubmit(true);
  }}
  disabled={!referenceNumber || submitting}
  className={`flex-1 px-4 py-2 rounded text-white 
    ${referenceNumber
      ? "bg-blue-600 hover:bg-blue-700"
      : "bg-gray-400 cursor-not-allowed"}`}>
  {submitting ? "Saving..." : (selectedProperty.refno || selectedProperty.pt5 || selectedProperty.pt_5 || selectedProperty.pt ? "Update" : "Save")}
</button>


          <button
            onClick={() => setShowRefModal(false)}
            className="flex-1 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded"
            data-click-id="pt5_cancel"
          >
            Cancel
          </button>
        </div>
      </motion.div>
    </motion.div>
  )}
</AnimatePresence>






<AnimatePresence>
  {showNotReassessModal && selectedProperty && (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.9 }}
        className="bg-white p-6 rounded-xl w-full max-w-md shadow-xl"
      >
        <h2 className="text-xl font-bold mb-4 text-gray-800">
          Reason for Not Reassessing
        </h2>
        
        <div className="mb-4">
          <label className="block font-medium text-gray-700 mb-2">
            {selectedProperty.refno || selectedProperty.pt5 || selectedProperty.pt_5 || selectedProperty.pt 
              ? "Edit PT-5 Serial Number" 
              : "PT-5 Serial Number"}
          </label>
          <input 
            type="text" 
            value={referenceNumber} 
            onChange={(e) => setReferenceNumber(e.target.value)}
            placeholder="PT-5 Serial Number" 
            className="w-full p-3 border rounded" 
          />
        </div>

        <textarea
          value={notReassessComment}
          onChange={(e) => setNotReassessComment(e.target.value)}
          rows={4}
          placeholder="Enter reason / comments..."
          className="w-full p-3 border rounded mb-3"
        />

        {!notReassessComment && (
          <p className="text-red-500 text-sm mb-3">
            Comment is required
          </p>
        )}

        <div className="flex gap-3">
          <button
            onClick={handleNotReassess}
            disabled={!notReassessComment || submitting}
            className={`flex-1 px-4 py-2 rounded text-white
              ${notReassessComment
                ? "bg-gray-700 hover:bg-gray-800"
                : "bg-gray-400 cursor-not-allowed"}`}
            data-click-id="reason_for_not_reassessing_confirm"
            data-property-id={selectedProperty.pin}
          >
            {submitting ? "Saving..." : (selectedProperty.refno || selectedProperty.pt5 || selectedProperty.pt_5 || selectedProperty.pt ? "Update" : "Confirm")}
          </button>

          <button
            onClick={() => setShowNotReassessModal(false)}
            className="flex-1 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded"
            data-click-id="reason_for_not_reassessing_cancel"
          >
            Cancel
          </button>
        </div>
      </motion.div>
    </motion.div>
  )}
</AnimatePresence>

        {/* Fullscreen Image Modal */}
        <AnimatePresence>
          {showFullImage && selectedProperty && selectedProperty.image && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60] p-4"
              onClick={() => {
                setShowFullImage(false);
                setRotation(0);
              }}
            >
              <div className="absolute top-6 right-6 flex space-x-4 z-10">
                <button
                  onClick={handleRotate}
                  className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all shadow-lg flex items-center justify-center"
                  title="Rotate Image"
                  data-click-id="pending_review_image_rotate"
                  data-property-id={selectedProperty.pin}
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
                {rotation !== 0 && (
                  <button
                    onClick={handleSaveRotation}
                    disabled={savingImageRotation}
                    className="p-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 rounded-full text-white transition-all shadow-lg flex items-center gap-2"
                    title="Save Rotation"
                  >
                    {savingImageRotation ? (
                      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <>
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </>
                    )}
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowFullImage(false);
                    setRotation(0);
                  }}
                  className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all shadow-lg flex items-center justify-center text-white text-2xl"
                  data-click-id="pending_review_image_close"
                >
                  ✕
                </button>
              </div>
              <motion.div
                initial={{ scale: 0.9, rotate: 0 }}
                animate={{ scale: 1, rotate: rotation }}
                exit={{ scale: 0.9 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="relative"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={appendCacheBuster(resolveImageUrl(selectedProperty.image), selectedProperty._imgBust)}
                  className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg shadow-2xl"
                  alt="Full Property"
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

      </main>
    </div>
  );
};

export default Assigned;
